import React from 'react'

import { ErrorMessage, Field, Form, Formik } from 'formik'
import ReactSelect from 'react-select'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { KeyButton } from '@/key-components/KeyButton'
import { KeyLabel } from '@/key-components/KeyLabel'
import { KeyInputField } from '@/key-components/formik/KeyInputField'
import { KeySelectField } from '@/key-components/formik/KeySelectField'
import { useGetTimezonesQuery } from '@/services/timezones'
import { useGetLoggedUserQuery } from '@/services/user'
import { useGetAllUsersDataQuery, useUpdateUserDataMutation } from '@/services/userData'
import { toastDefaultOptions } from '@/utils/toast'
import { getUserFullNameOrEmail } from '@/utils/user'

const editUserSchema = yup.object().shape({
  checkEmail: yup.boolean(),
  username: yup.string().required('Username is required'),
  full_name: yup.string(),
})

export const UserProfile = () => {
  const { data: user } = useGetLoggedUserQuery()

  const [updateUserData] = useUpdateUserDataMutation()

  const { data: timezones } = useGetTimezonesQuery()

  const { data: users, isLoading: isLoadingUsers } = useGetAllUsersDataQuery(true)

  const initialValues = {
    id: user?.id,
    checkEmail: false,
    username: user?.username || '',
    data: {
      phone: user?.data?.phone || '',
      meeting_link: user?.data?.meeting_link || '',
      staff_notifications_email: user?.data?.staff_notifications_email,
      staff_notifications_slack: user?.data?.staff_notifications_slack,
      staff_notifications_system_notifications:
        user?.data?.staff_notifications_system_notifications,
      staff_notifications_user_mention: user?.data?.staff_notifications_user_mention,
      timezone: user?.data?.timezone
        ? user?.data?.timezone
        : timezones
          ? Object.keys(timezones).find(
              (timezone) => timezone === Intl.DateTimeFormat().resolvedOptions().timeZone
            )
          : '',
      out_of_office: user?.data.out_of_office,
      manager: user?.data?.manager,
    },
    full_name: user?.data.full_name,
  }

  const userList =
    users?.map((user) => ({
      label: getUserFullNameOrEmail(user),
      value: user.id,
    })) ?? []

  const managerObj = users?.find((u) => u.id === user?.data?.manager)

  return (
    <>
      <div className='bg-white shadow p-5'>
        <Formik<typeof initialValues>
          initialValues={initialValues}
          validationSchema={editUserSchema}
          enableReinitialize
          onSubmit={async (values) => {
            const nameParts = values?.full_name.split(' ')

            const first_name = nameParts[0]
            const last_name = nameParts.slice(1).join(' ')

            const response = await updateUserData({
              ...values,
              first_name: first_name,
              last_name: last_name || '',
            })

            if (response) toast.success('User Profile Updated!', { ...toastDefaultOptions })
          }}
        >
          {({ values, setFieldValue, isSubmitting, isValid, resetForm, handleChange, dirty }) => {
            return (
              <Form className='flex flex-col gap-8'>
                <div>
                  <h1 className='text-primary font-bold text-base mb-1'>Personal Info</h1>
                  <KeyInputField
                    className='w-full'
                    placeholder={'Full name'}
                    name='full_name'
                  />
                  <ErrorMessage name='full_name'>
                    {(msg) => <small className='text-error'>{msg}</small>}
                  </ErrorMessage>
                </div>
                <div>
                  <h1 className='text-primary font-bold text-base mb-1'>Phone</h1>
                  <KeyInputField
                    className='w-full'
                    placeholder={'Phone'}
                    name={'data.phone'}
                  />
                  <ErrorMessage name='phone'>
                    {(msg) => <small className='text-error'>{msg}</small>}
                  </ErrorMessage>
                </div>

                <div>
                  <h1 className='text-primary font-bold text-base mb-1'>Meeting Link</h1>
                  <KeyInputField
                    className='w-full'
                    placeholder={'Meeting Link'}
                    name={'data.meeting_link'}
                  />
                  <ErrorMessage name='meeting_link'>
                    {(msg) => <small className='text-error'>{msg}</small>}
                  </ErrorMessage>
                </div>

                <div>
                  <h1 className='text-primary font-bold text-base mb-1'>Manager</h1>
                  <Field
                    key={managerObj?.id}
                    className='w-full'
                    placeholder='Manager'
                    name='data.manager'
                    component={ReactSelect}
                    options={userList}
                    defaultValue={{
                      value: managerObj?.id,
                      label: managerObj?.data?.full_name,
                    }}
                    isLoading={isLoadingUsers}
                    isDisabled={
                      isSubmitting ||
                      isLoadingUsers ||
                      !userList.length ||
                      user.data.role !== 'admin'
                    }
                    onChange={(option) => {
                      handleChange({
                        target: {
                          name: 'data.manager',
                          value: option?.value,
                        },
                      })
                    }}
                  />
                  <ErrorMessage name='manager'>
                    {(msg) => <small className='text-error'>{msg}</small>}
                  </ErrorMessage>
                </div>

                <div>
                  <h1 className='text-primary font-bold text-base mb-1'>Out of Office</h1>
                  <span className='pr-2'>Off</span>
                  <input
                    type='checkbox'
                    className='toggle focus:toggle'
                    name='data.out_of_office'
                    checked={values?.data?.out_of_office}
                    onChange={() =>
                      setFieldValue('data.out_of_office', !values?.data?.out_of_office)
                    }
                  />
                  <span className='pl-2'>On</span>
                  <div className='pt-2'>
                    <span>
                      While your out of office is on you will not be included in round robins.
                    </span>
                  </div>
                </div>

                {timezones && (
                  <div>
                    <h1 className='text-primary font-bold text-base mb-1'>Time Zone</h1>
                    <Field
                      id='timezone'
                      name='data.timezone'
                      component={KeySelectField}
                      options={Object?.entries(timezones).map((timezone) => ({
                        value: timezone[0],
                        label: timezone[1],
                      }))}
                    />
                  </div>
                )}

                <div className='grid gap-3'>
                  <h1 className='text-primary font-bold text-base mb-1'>
                    Staff Notifications Destinations
                  </h1>
                  <label className='cursor-pointer'>
                    <Field
                      className='checkbox checkbox-primary mr-1'
                      type='checkbox'
                      readOnly={isSubmitting}
                      name={'data.staff_notifications_email'}
                    />
                    Email
                  </label>
                  <label className='cursor-pointer'>
                    <Field
                      className='checkbox checkbox-primary mr-1'
                      type='checkbox'
                      readOnly={isSubmitting}
                      name={'data.staff_notifications_slack'}
                    />
                    Slack
                  </label>
                  <h1 className='text-primary font-bold text-base mb-1 mt-3'>
                    System Notifications Types
                  </h1>
                  <label className='cursor-pointer'>
                    <Field
                      className='checkbox checkbox-primary mr-1'
                      type='checkbox'
                      readOnly={isSubmitting}
                      name={'data.staff_notifications_system_notifications'}
                    />
                    System Notifications
                  </label>
                  <label className='cursor-pointer'>
                    <Field
                      className='checkbox checkbox-primary mr-1'
                      type='checkbox'
                      readOnly={isSubmitting}
                      name={'data.staff_notifications_user_mention'}
                    />
                    User Mentions
                  </label>
                </div>

                <div>
                  <div className='flex gap-2 items-center w-full mt-4'>
                    <button
                      type='submit'
                      className='btn btn-primary btn-sm'
                      disabled={!isValid || isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>

                    <button
                      className='btn btn-secondary btn-sm'
                      onClick={() => resetForm()}
                      type='button'
                      disabled={!dirty}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </>
  )
}
